import axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { Container, Row, Col, Card, Button } from 'react-bootstrap'
import toast from 'react-hot-toast'
import styled, { keyframes } from 'styled-components'
import Cookies from 'js-cookie'
import { BASE_URL } from '../constants'
import { buildersInfo } from '../config/builderInfo'
import {
  FaRegCalendarAlt,
  FaUserAlt,
  FaClipboard,
  FaRupeeSign,
  FaHashtag,
  FaSquare,
  FaPhone,
  FaMapMarkerAlt,
  FaCreditCard,
  FaRulerCombined,
} from 'react-icons/fa'
import { Link, useLocation, useParams } from 'react-router-dom'
import html2pdf from 'html2pdf.js'
import {
  PDF_EXPORT_OPTIONS,
  maxWidthForPdfImage,
  toOptimizedDataUrl,
} from '../utils/pdfImages'

// Get the auth token from cookies
const token = Cookies.get('authToken')

// Create an Axios instance with default headers
const axiosInstance = axios.create({
  baseURL: BASE_URL, // Set your API base URL
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`, // Set the Authorization header for all requests
  },
})
// Define the bounce animation using styled-components
const bounceIn = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
`

const AnimatedCardBody = styled(Card.Body)`
  &.bounce-in {
    animation: ${bounceIn} 0.8s ease forwards;
  }
`

const ReceiptFrame = styled.div`
  --pad-x: 12mm;
  --pad-y: 12mm;
  padding: 0 var(--pad-x) var(--pad-y) var(--pad-x);
  background: #fff;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  height: 297mm; /* A4 portrait height (border-box includes padding) */
  display: flex;
  flex-direction: column;
`

const ReceiptHeader = styled.div`
  width: calc(100% + 2 * var(--pad-x));
  margin-left: calc(-1 * var(--pad-x));
  margin-right: calc(-1 * var(--pad-x));
  margin-bottom: 40px;
  background-color: rgba(142, 155, 86, 0.063);
  border-bottom: 1px solid rgba(77, 92, 43, 0.25);
  padding: 14px var(--pad-x) 30px var(--pad-x);
  min-height: 110px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
`

const ReceiptLogo = styled.img`
  width: auto !important;
  height: 106px !important;
  max-height: 106px !important;
  display: block;
  margin-left: -18px;
`

const ReceiptHeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
`

const ReceiptCompanyTitle = styled.div`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: rgb(77, 92, 43);
  text-transform: uppercase;
`

const ReceiptCompanySubtitle = styled.div`
  margin-top: 4px;
  font-size: 18px;
  font-weight: 600;
  color: rgb(77, 92, 43);
`

const ReceiptMain = styled.div`
  margin-top: 8px;
  flex: 1 1 auto; /* grows to absorb all available vertical space */
  display: flex;
  flex-direction: column;
`

const ReceiptFooterBg = styled.div`
  position: relative;
  width: calc(100% + 2 * var(--pad-x));
  margin-left: calc(-1 * var(--pad-x));
  margin-right: calc(-1 * var(--pad-x));
  margin-bottom: calc(-1 * var(--pad-y));
  margin-top: 0;
  overflow: hidden;
  padding: 22px var(--pad-x) 26px var(--pad-x);
  box-sizing: border-box;
`

const ReceiptFooterTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: rgb(77, 92, 43);
  margin-bottom: 6px;
`

export default function BookingPrintpage() {
  const { bookingId } = useParams()
  console.log(bookingId)
  const [booking, setBooking] = useState(null)
  const [customer, setCustomer] = useState(null)
  const [plot, setPlot] = useState(null)
  const [site, setSite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [animate, setAnimate] = useState(false) // State for animation
  const printRef = useRef();
  // const location = useLocation()
  // Parse query parameters
  // const queryParams = new URLSearchParams(location.search)
  // const isPrintMode = queryParams.get('source') === 'slip'

  useEffect(() => {
    const fetchBookingAndCustomerDetails = async () => {
      setLoading(true)
      const token = Cookies.get('authToken') // Retrieve token from cookies

      try {
        const bookingResponse = await axiosInstance.get(BASE_URL+
          `/get/booking?id=${bookingId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )
        const bookingData = bookingResponse.data.data[0]
        console.log('Bookings response', bookingData)
        setBooking(bookingData)

        const customerResponse = await axiosInstance.get(
          BASE_URL+'/customers',
        )

        const foundCustomer = customerResponse.data.data.find(
          (customer) => customer.id === bookingData.customer_id,
        )

        if (foundCustomer) {
          console.log('Customer response', customerResponse)
          setCustomer(foundCustomer)
        } else {
          toast.error('Customer not found')
        }

        const plotResponse = await axiosInstance.get(
          BASE_URL+'/plot/search',
        )

        const foundPlot = plotResponse.data.data.find(
          (plot) => plot.id === bookingData.plot_id,
        )

        if (foundPlot) {
          console.log('Plot response', plotResponse)
          setPlot(foundPlot)
        }

        const siteResponse = await axiosInstance.get(
          BASE_URL+'/sites',
        )

        const foundSite = siteResponse.data.data.find(
          (site) => site.id === foundPlot.site_id,
        )

        if (foundSite) {
          console.log('Site response', siteResponse)
          setSite(foundSite)
        } else {
          toast.error('Site not found')
        }
      } catch (error) {
        toast.error('Failed to fetch booking details')
      } finally {
        setLoading(false)
        // Trigger animation after data is loaded
        setAnimate(true)
      }
    }

    fetchBookingAndCustomerDetails()
  }, [bookingId])

  const handlePrint = () => window.print()

  const generatePDF = async () => {
    const element = document.getElementById('print_page')
    if (!element) { toast.error('Receipt element not found'); return }

    setPdfLoading(true)

    try {
      // Downscale + JPEG data URLs so html2canvas stays untainted and PDFs stay small
      const imgs = Array.from(element.querySelectorAll('img[src]'))
      const origSrcs = imgs.map((img) => img.getAttribute('src'))

      await Promise.all(
        imgs.map(async (img, i) => {
          try {
            img.src = await toOptimizedDataUrl(origSrcs[i], maxWidthForPdfImage(img))
            if (typeof img.decode === 'function') {
              await img.decode().catch(() => {})
            }
          } catch {
            // keep original src if fetch fails — pdf may have blank image but won't crash
          }
        })
      )

      // Fix logo dimensions: html2canvas cannot resolve "width: auto", so we
      // explicitly compute the correct pixel width from the image's natural
      // aspect ratio at the target height (106px) to match the print view.
      const logoImg = element.querySelector('img[alt="Neelkanth Shivay Builders"]')
      const origLogoWidth = logoImg ? logoImg.style.width : null
      const origLogoHeight = logoImg ? logoImg.style.height : null
      if (logoImg && logoImg.naturalWidth && logoImg.naturalHeight) {
        const targetHeight = 106
        const computedWidth = Math.round((logoImg.naturalWidth / logoImg.naturalHeight) * targetHeight)
        logoImg.style.width = `${computedWidth}px`
        logoImg.style.height = `${targetHeight}px`
      }

      element.classList.add('pdf-style')

      await html2pdf()
        .from(element)
        .set({
          margin: [0, 0, 0, 0],
          filename: `plot-booking-details_${booking.id}_${plot.plot_number}.pdf`,
          ...PDF_EXPORT_OPTIONS,
        })
        .save()

      // Restore original src values and logo dimensions
      imgs.forEach((img, i) => { img.src = origSrcs[i] })
      if (logoImg) {
        logoImg.style.width = origLogoWidth || ''
        logoImg.style.height = origLogoHeight || ''
      }
      element.classList.remove('pdf-style')
      setPdfLoading(false)
      toast.success('PDF downloaded!')

    } catch (err) {
      console.error('PDF generation failed:', err)
      document.getElementById('print_page')?.classList.remove('pdf-style')
      setPdfLoading(false)
      toast.error('Failed to generate PDF. Please try again.')
    }
  }

  if (loading) {
    return <h2>Loading...</h2>
  }

  if (!booking || !customer || !plot || !site) {
    return <h2>Data not available</h2>
  }

// place this function inside your component that has `printRef`
  return (
    <>
      <style>{`
        /* ── PDF download (html2pdf adds .pdf-style) ─────────────────────── */
        #print_page.pdf-style {
          border-radius: 0 !important;
          overflow: visible !important;
          box-shadow: none !important;
          border: none !important;
        }
        #print_page.pdf-style .card-body { padding: 0 !important; }
        #print_page.pdf-style .container, #print_page.pdf-style .container-fluid {
          padding-left: 0 !important;
          padding-right: 0 !important;
          max-width: 100% !important;
        }
        #print_page.pdf-style .row { margin-left: 0 !important; margin-right: 0 !important; }
        #print_page.pdf-style img { max-width: 100% !important; }
        #print_page.pdf-style img[alt="Neelkanth Shivay Builders"] {
          width: auto !important;
          height: 106px !important;
          max-height: 106px !important;
        }

        /* ── Browser Print (window.print()) ──────────────────────────────── */
        @media print {
          @page { size: A4 portrait; margin: 0mm; }

          /* 1. Completely remove sidebar and topbar — they have fixed position
                and their own z-index/background which bleeds into print */
          #sidebar,
          header.topbar { display: none !important; }

          /* 2. Reset the layout wrappers so they add no space or background */
          .main-layout,
          .content-area,
          .main-section {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            background: transparent !important;
            overflow: visible !important;
          }

          /* 3. Hide everything's content */
          body * { visibility: hidden !important; }

          /* 4. Reveal only the receipt */
          #print_page,
          #print_page * { visibility: visible !important; }

          /* 5. Stamp the receipt flush at the top-left corner of the A4 sheet,
                above everything else (sidebar z-index was 1050) */
          #print_page {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            z-index: 99999 !important;
          }

          /* 6. Strip card-body padding */
          .card-body { padding: 0 !important; }

          /* 7. Hide buttons */
          .no-print { display: none !important; visibility: hidden !important; }

          /* 8. Force browsers to print background colours and images */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <AnimatedCardBody className={animate ? 'bounce-in' : ''}>
        <Container>
          <Row className="justify-content-end">
            <Col xs={12} md={10} lg={10}>
              <Card className="shadow-lg p-0" id="print_page" ref={printRef}>
                <Card.Body className={animate ? 'bounce-in p-0' : 'p-0'}>
                  <ReceiptFrame>
                    {/* Header */}
                    <ReceiptHeader>
                      <ReceiptLogo src="https://booking.neelkanthshivay.com/ishanbuilder_logo.png" alt="Neelkanth Shivay Builders" />

                      <ReceiptHeaderCenter>
                        <ReceiptCompanyTitle>NEELKANTH SHIVAY</ReceiptCompanyTitle>
                        <ReceiptCompanySubtitle>Infra Developers Pvt Ltd</ReceiptCompanySubtitle>
                      </ReceiptHeaderCenter>
                    </ReceiptHeader>

                    {/* Main Content */}
                    <ReceiptMain>
                      <div>
                        <h2
                          className="text-center fw-bold text-success"
                          style={{ marginBottom: '6px' }}
                        >
                          🎉&nbsp;Congratulations&nbsp;&nbsp;🎉
                        </h2>
                        <p
                          className="text-muted text-center"
                          style={{ marginBottom: '0px' }}
                        >
                          Your plot has been successfully booked
                        </p>
                        <h4
                          className="text-primary text-center fw-bold text-primary"
                          style={{ marginBottom: '16px', marginTop: '10px' }}
                        >
                          {site.name ? site.name.toUpperCase() : 'N/A'}
                        </h4>
                      </div>

                      <div
                        className="fluid mt-3 mx-auto"
                        style={{ fontSize: '14px' }}
                      >
                        <Container className="mx-auto">
                          <Row className="mx-auto">
                            <Col xs={12} sm={6} className="text-start">
                              <p>
                                <FaClipboard /> <strong>Booking ID : </strong>
                                {booking.id}
                              </p>
                              <p>
                                <FaUserAlt /> <strong>Booked by : </strong>
                                {customer.title}
                                {'.'} {customer.name || 'N/A'}
                              </p>
                              <p>
                                <FaUserAlt /> <strong>Guardian Name : </strong>
                                {customer.guardian_title}
                                {'.'} {customer.guardian_name || 'N/A'}
                              </p>
                              <p>
                                <FaRegCalendarAlt /> <strong>Booking Date: </strong>
                                {new Date(booking.booking_date).toLocaleDateString(
                                  'en-GB',
                                  {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  },
                                )}
                              </p>
                            </Col>
                            <Col xs={12} sm={4} className="text-start  mx-auto">
                              <p>
                                <FaCreditCard /> <strong>Payment mode : </strong>
                                {booking.payment_method || 'N/A'}
                              </p>
                              <p>
                                <FaSquare /> <strong>Block : </strong>
                                {plot.block || 'N/A'}
                              </p>
                              <p>
                                <FaRulerCombined /> <strong>Plot Area : </strong>
                                {plot.plot_size || 'N/A'}
                              </p>
                              <p>
                                <FaHashtag /> <strong>Plot Number : </strong>
                                {plot.plot_number || 'N/A'}
                              </p>
                              <p className="text-danger" style={{ marginBottom: 0 }}>
                                <FaRupeeSign /> <strong>Booking Amount : </strong>₹{' '}
                                {booking.booking_price}
                              </p>
                            </Col>
                          </Row>
                          {/* <Row className="text-start mx-0" style={{ marginTop: '6px' }}>
                            <Col>
                              <p style={{ marginBottom: 0 }}>
                                <FaMapMarkerAlt /> <strong>Address : </strong>
                                {customer.address}
                              </p>
                            </Col>
                          </Row> */}
                        </Container>
                      </div>

                      {/* Company name pins to the bottom of ReceiptMain via margin-top: auto */}
                      <div className="text-start" style={{ marginTop: 'auto', paddingTop: '12px', marginBottom: '100px' }}>
                        <h5 className="fw-bold" style={{ marginTop: '0px', marginBottom: '4px' }}>
                          {buildersInfo.name}
                        </h5>
                        <p style={{ marginBottom: '0px' }}>[ Authorized Signature]</p>
                      </div>
                    </ReceiptMain>

                    {/* Footer (full-bleed background image with overlay) */}
                    <ReceiptFooterBg className="receipt-footer-bg">
                      {/* Real DOM background image — rendered by html2canvas unlike CSS background-image */}
                      <img
                        src="https://booking.neelkanthshivay.com/page_media/builders-backphoto.png"
                        alt=""
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          top: 0, left: 0,
                          width: '100%', height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          zIndex: 0,
                          display: 'block',
                          pointerEvents: 'none',
                        }}
                      />
                      {/* Real DOM gradient overlay — html2canvas ignores ::before, so use a real div */}
                      <div
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          top: 0, left: 0,
                          width: '100%', height: '100%',
                          background: 'linear-gradient(to bottom, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.82) 52%, rgba(255,255,255,0.96) 100%)',
                          zIndex: 1,
                          pointerEvents: 'none',
                        }}
                      />
                      {/* Content sits above the image + overlay */}
                      <div style={{ position: 'relative', zIndex: 2 }}>
                        <ReceiptFooterTitle>TERMS & CONDITIONS</ReceiptFooterTitle>

                        <p className="fw-bold" style={{ marginBottom: '4px', fontSize: '8.5px' }}>
                          <i>
                            We are committed to providing you the best in class
                            service.
                          </i>
                        </p>

                        <h6 style={{ fontWeight: 700, marginBottom: '3px', fontSize: '9px' }}>Terms:</h6>
                        <ol style={{ marginBottom: '10px', fontSize: '8.5px', lineHeight: '1.4' }}>
                          <li>This receipt is subjected to realization of cheque/draft</li>
                          <li>Allotted plot will be cancelled if commitment is not met.</li>
                          <li>In case of refund, 30% will be deducted.</li>
                          <li>Booking amount is non-refundable.</li>
                          <li>This receipt is for record purposes only.</li>
                        </ol>

                        <div
                          style={{
                            textAlign: 'center',
                            fontSize: '18px',
                            color: '#333',
                          }}
                        >
                          For more details, please mail us at{' '}
                          <a
                            href="mailto:info@neelkanthshivay.com"
                            style={{ textDecoration: 'none', color: '#0d6efd' }}
                          >
                            info@neelkanthshivay.com
                          </a>{' '}
                          or visit our website{' '}
                          <a
                            href="https://neelkanthshivay.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: '#0d6efd' }}
                          >
                            https://neelkanthshivay.com/
                          </a>
                          <div style={{ marginTop: '6px', fontSize: '16px', fontWeight: '700', color: '#333' }}>
                            Office: {buildersInfo.address}
                          </div>
                        </div>
                      </div>
                    </ReceiptFooterBg>
                  </ReceiptFrame>
                </Card.Body>
              </Card>

              <Row className="mt-4 justify-content-end">
                <Col xs="auto" className="d-flex gap-1">
                  <Button
                    variant="outline-secondary"
                    className="mb-2 no-print"
                    onClick={handlePrint}
                  >
                    <i className="bi bi-file-earmark-pdf"></i> Print
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="fw-bold mb-2 no-print"
                    onClick={generatePDF}
                    disabled={pdfLoading}
                  >
                    <i className="bi bi-file-earmark-pdf"></i>{' '}
                    {pdfLoading ? 'Generating...' : 'Download PDF'}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </AnimatedCardBody>
    </>
  )
}
